"use client";

import { Search, Plus, type LucideIcon } from "@/components/icons";
import Link from "next/link";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export interface EmptyStateAction {
 label: string;
 onClick?: () => void;
 href?: string;
 icon?: LucideIcon;
}

export interface EmptyStateProps {
 icon?: LucideIcon;
 title: string;
 description?: string;
 action?: EmptyStateAction;
 secondaryAction?: EmptyStateAction;
 children?: ReactNode;
 variant?: "default" | "compact" | "card" | "table";
 className?: string;
}

export function EmptyState({
 icon: Icon = Search,
 title,
 description,
 action,
 secondaryAction,
 children,
 variant = "default",
 className,
}: EmptyStateProps) {
 const ActionIcon = action?.icon ?? Plus;

 return (
  <div
   className={cn(
    "flex flex-col items-center justify-center text-center",
    variant === "default" && "rounded-sm border-2 border-dashed px-6 py-12",
    variant === "compact" && "px-4 py-8",
    variant === "card" && "rounded-sm border bg-card px-6 py-12",
    variant === "table" &&
     "rounded-lg border border-border bg-card py-16",
    className,
   )}
  >
   <EmptyStateIcon icon={Icon} variant={variant} />
   <EmptyStateText title={title} description={description} variant={variant} />

   {(action || secondaryAction) && (
    <div className="mt-6 flex items-center gap-3">
     {action && (
      <ActionButton action={action} icon={ActionIcon} variant={variant} />
     )}
     {secondaryAction && (
      <ActionButton
       action={secondaryAction}
       icon={secondaryAction.icon}
       variant={variant}
       secondary
      />
     )}
    </div>
   )}

   {children && <div className="mt-6">{children}</div>}
  </div>
 );
}

type EmptyStateVariant = NonNullable<EmptyStateProps["variant"]>;

function EmptyStateIcon({
 icon: Icon,
 variant,
}: {
 icon: LucideIcon;
 variant: EmptyStateVariant;
}) {
 if (variant === "table") {
  return <Icon className="h-10 w-10 mb-3 text-muted-foreground/50" />;
 }
 return (
  <div
   className={cn(
    "flex items-center justify-center rounded-sm border border-border bg-card",
    variant === "compact" ? "h-10 w-10" : "h-12 w-12",
   )}
  >
   <Icon
    className={cn(
     "text-muted-foreground",
     variant === "compact" ? "h-5 w-5" : "h-6 w-6",
    )}
   />
  </div>
 );
}

function EmptyStateText({
 title,
 description,
 variant,
}: {
 title: string;
 description?: string;
 variant: EmptyStateVariant;
}) {
 return (
  <>
   {variant === "table" ? (
    <p className="text-body font-medium text-muted-foreground">{title}</p>
   ) : (
    <h3
     className={cn(
      variant === "compact" ? "mt-3 text-body font-semibold" : "mt-4 text-title",
     )}
    >
     {title}
    </h3>
   )}

   {description && (
    <p
     className={cn(
      variant === "table"
       ? "text-caption text-muted-foreground/70 mt-1"
       : "mt-2 max-w-sm text-muted-foreground",
      variant === "compact" && "text-caption",
      !["table", "compact"].includes(variant) && "text-body",
     )}
    >
     {description}
    </p>
   )}
  </>
 );
}

interface ActionButtonProps {
 action: EmptyStateAction;
 icon?: LucideIcon;
 variant: EmptyStateProps["variant"];
 secondary?: boolean;
}

function ActionButton({
 action,
 icon: Icon,
 variant,
 secondary,
}: ActionButtonProps) {
 const size = variant === "compact" ? "sm" : "default";
 const buttonVariant = secondary ? "outline" : "primary";

 const content = (
  <>
   {Icon && (
    <Icon
     className={cn(size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4", "mr-2")}
    />
   )}
   {action.label}
  </>
 );

 if (action.href) {
  return (
   <Button asChild size={size} variant={buttonVariant}>
    <Link href={action.href}>{content}</Link>
   </Button>
  );
 }

 return (
  <Button onClick={action.onClick} size={size} variant={buttonVariant}>
   {content}
  </Button>
 );
}
